import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Select, FormInstance, Row, Col } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { StyledPhoneInput } from "../styles";
import { getCountries } from "../../../../redux/features/generaleWebData/generaleWebDataAsyncThunk";
import api from "../../../../utils/constants/api";
import { capitalizeWord } from "../../../../utils/helpers/capitalizeWord";
import { FormWrapper } from "../../../../components/styles";
import { City, State } from "../../../../types";
import { ApiResponse } from "../../../../types/APIResponse";
import "react-phone-input-2/lib/style.css";
import { sortOptionWithTopValues, topCountriesId } from "../../../../utils/helpers/sortOptionsAlphabetically";
import CustomInput, { TextArea } from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import { Report } from "../../../../types";
import GoogleAutocompleteInput, { Place } from "../../../../components/GoogleAutocomplete";
import { countries, province, state } from "../../../../utils/data/countries";

interface UserDetailsFormProps {
  form: FormInstance;
  report: Report | null;
}

const UserDetailsForm: React.FC<UserDetailsFormProps> = ({ form, report }) => {
  const dispatch = useAppDispatch();
  const watchedValues = Form.useWatch([], form);

  useEffect(() => {
    const formValues = form.getFieldsValue();
    if (report?.user && !formValues?.email) {
      const userInfo = {
        ...report.user,
        city: report.user?.city || null,
        state: report.user?.state || null,
        country: report.user?.country || null,
        region: report.user?.countryCode || null,
        stateCode: report.user?.stateCode || null,
        countryCode: report.user?.countryCode || null,
        placeId: report.user?.placeId || null,
        tempPlaceId: report.user?.placeId || null,
        internalEmail: report.user?.internalEmail || null,
      };
      form.setFieldsValue({ ...userInfo });
    } else {
      form.setFieldsValue(formValues);
    }
  }, [report]);

  const renderCountryOptions = useMemo(() => {
    if (countries?.length > 0) {
      const sortedCountries = sortOptionWithTopValues(countries, "name", topCountriesId);
      return sortedCountries.map((country) => ({
        key: country.code,
        value: country.code,
        label: country.name,
      }));
    }
    return [];
  }, [countries]);

  const renderStateOptions = useMemo(() => {
    if (state?.length > 0) {
      return state.map((state) => ({
        key: state.stateCode,
        value: state.stateCode,
        label: state.name,
      }));
    }
    return [];
  }, [state]);

  const renderProvinceOptions = useMemo(() => {
    if (province?.length > 0) {
      return province.map((province) => ({
        key: province.stateCode,
        value: province.stateCode,
        label: province.name,
      }));
    }
    return [];
  }, [province]);

  return (
    <FormWrapper form={form} layout="vertical">
      <Row gutter={16} className="row-gap">
        <Col span={24} md={12}>
          <CustomInput
            label="First Name"
            name="firstName"
            placeholder="First Name"
            rules={[{ required: true, message: "Please enter your first name!" }]}
            fontWeight="700"
            onChange={(e: any) => {
              form.setFieldsValue({ firstName: e.target.value });
            }}
            onBlur={(e: any) => {
              const capitalizedValue = capitalizeWord(e.target.value);
              form.setFieldsValue({ firstName: capitalizedValue });
            }}
          />
        </Col>
        <Col flex="auto" md={12}>
          <CustomInput
            label="Last Name"
            name="lastName"
            placeholder="Last Name"
            rules={[{ required: true, message: "Please enter your last name!" }]}
            fontWeight="700"
            onChange={(e: any) => {
              form.setFieldsValue({ lastName: e.target.value });
            }}
            onBlur={(e: any) => {
              const capitalizedValue = capitalizeWord(e.target.value);
              form.setFieldsValue({ lastName: capitalizedValue });
            }}
          />
        </Col>
      </Row>

      <Row gutter={16} className="row-gap">
        <Col span={24} md={12}>
          <CustomInput
            label="Email"
            name="email"
            placeholder="Email"
            rules={[
              { required: true, message: "Please enter your email!" },
              { type: "email", message: "Please enter valid email" },
            ]}
            fontWeight="700"
            onChange={(e: any) => {
              form.setFieldsValue({ email: e.target.value?.trim()?.toLowerCase() });
            }}
          />
        </Col>
        <Col span={24} md={12}>
          {/* internalEmail hidden from admin dashboard */}
        </Col>
      </Row>

      <Row gutter={16} className="row-gap">
        <Col span={24} md={12}>
          <Form.Item label={<span style={{ fontWeight: "700" }}>Phone Number</span>} name="phone" rules={[{ required: true, message: "Please enter your phone number!" }]}>
            <StyledPhoneInput
              country={"us"}
              inputProps={{
                placeholder: "Enter a Phone Number",
              }}
              inputStyle={{
                width: "100%",
                padding: "10px",
                borderRadius: "3px",
                fontSize: "15px",
                paddingLeft: "40px",
                background: "#fff",
                height: "40px",
                fontWeight: "400",
                color: "#333333",
              }}
              buttonStyle={{
                borderRight: "none",
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} className="row-gap">
        <Col span={24} md={24}>
          <TextArea
            label="Address 1"
            name="address1"
            placeholder="Address"
            rules={[{ required: true, message: "Please enter your address!" }]}
            fontWeight="700"
            onChange={(e: any) => {
              form.setFieldsValue({ address1: e.target.value });
            }}
          />
        </Col>
      </Row>
      <Row gutter={16} className="row-gap">
        <Col span={24} md={12}>
          <Dropdown
            fontWeight="700"
            label={"Country"}
            name="region"
            options={renderCountryOptions}
            rules={[
              {
                required: true,
                message: "Please select country",
              },
            ]}
            onSelect={(value) => {
              const selectedOption = renderCountryOptions.find(opt => opt.value === value);
              form.setFieldsValue({
                country: selectedOption?.label,
                region: selectedOption?.value,
                countryCode: selectedOption?.value,
                state: undefined,
                stateCode: undefined,
              });
            }}
          />
        </Col>
        <Col span={24} md={12}>
          {form.getFieldValue("region") === 'US' ? (
            <Dropdown
              fontWeight="700"
              label={"State"}
              placeholder={"State"}
              name="state"
              options={renderStateOptions}
              onSelect={(value) => {
                const selectedOption = renderStateOptions.find(opt => opt.value === value);
                form.setFieldsValue({
                  state: selectedOption?.label,
                  stateCode: selectedOption?.value,
                });
              }}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const region = getFieldValue('region');
                    if (region === 'US' && !value) {
                      return Promise.reject(new Error("please select state"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          ) : form.getFieldValue("region") === 'CA' ? (
            <Dropdown
              fontWeight="700"
              label={"Province"}
              placeholder={"Province"}
              name="state"
              options={renderProvinceOptions}
              onSelect={(value) => {
                const selectedOption = renderProvinceOptions.find(opt => opt.value === value);
                form.setFieldsValue({
                  state: selectedOption?.label,
                  stateCode: selectedOption?.value,
                });
              }}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const region = getFieldValue('region');
                    if (region === 'CA' && !value) {
                      return Promise.reject(new Error("please select province"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          ) : (
            <CustomInput
              fontWeight="700"
              label={"State"}
              name="state"
              placeholder="State"
              rules={[{ required: false, message: "Please enter your state!" }]}
              onChange={(e: any) => {
                form.setFieldsValue({ state: e.target.value });
              }}
            />
          )}
        </Col>
      </Row>
      <Row gutter={16} className="row-gap">
        <Col span={24} md={12}>
          <CustomInput
            fontWeight="700"
            label={"City"}
            name="city"
            placeholder="City"
            rules={[
              {
                required: true,
                message: "Please select your city!",
              },
            ]}
          />
        </Col>
        <Col span={24} md={12}>
          <CustomInput
            label="ZIP"
            name="zip"
            placeholder="ZIP"
            rules={[{ required: false, message: "Please enter your ZIP code!" }]}
            fontWeight="400"
            onChange={(e: any) => {
              form.setFieldsValue({ zip: e.target.value });
            }}
          />
        </Col>
      </Row>
    </FormWrapper>
  );
};

export default UserDetailsForm;
